import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Result from '../../components/result/Result'
import Question from '../../components/question/Question'
import Skeleton from 'react-loading-skeleton';
import Loading from '../../components/loading/Loading';
import './Exercise.css'; // Add CSS styles as needed
import { AiOutlineClose, AiOutlineCheck, AiOutlineClockCircle, AiOutlineBulb } from 'react-icons/ai';

const shuffleChoices = (array) => {
    return array
        .map((item) => ({ ...item, sort: Math.random() })) // Add random sort key
        .sort((a, b) => a.sort - b.sort) // Sort by random key
        .map(({ sort, ...rest }) => rest); // Remove the random key
};
export function Exercise({ topicId, type_='exercise', onBackToTopic }) {

    const navigate = useNavigate();

    const { id: routeTopicId } = useParams(); // Get topic ID from route params DEPRECATED, exercise page is not a path/route
    // Prefer prop id if available; if not, fallback to route id.
    const id = topicId ? topicId : routeTopicId;

    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState([]); // Support multiple selections
    const [showHint, setShowHint] = useState(false);
    const [feedback, setFeedback] = useState(null); // { correct: boolean, justification: string }
    const [isSubmitPhase, setIsSubmitPhase] = useState(false); // Track if we're in the "Submit" or "Next" phase

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch questions from the backend
    const fetchQuestions = async () => {
        try {
            setIsLoading(true);
            setError(null);

            console.log(id);

            const response = await fetch(`/api/question/${id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type_,
                }),
            });

            console.log(response);

            if (!response.ok) {
                throw new Error('Failed to fetch questions');
            }

            let data = await response.json();

            // Shuffle the choices
            data.forEach((question, index) => {
                question.choices = shuffleChoices(question.choices); 
            });

            setQuestions(data);

            // Set time limit based on metadata of the first question
            const totalTime = data.reduce(
                (total, q) => total + (q.metadata?.timeLimit || 0),
                0
            );
            setTimeRemaining(totalTime > 0 ? totalTime : null);

        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchQuestions();
    }, [id]);

    useEffect(() => {
        if (timeRemaining === null || timeRemaining <= 0 || isSubmitPhase) return; // Stop countdown during Submit Phase

        const timer = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev === 1) {
                    handleTimeout(); // Handle timeout if time reaches zero
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer); // Clear timer when component unmounts or timeRemaining changes
    }, [timeRemaining, isSubmitPhase]);

    const handleTimeout = () => {
        const currentQuestion = questions[currentQuestionIndex];

        // Show feedback that the question was not answered and is incorrect
        setFeedback({
            correct: false,
            justification: `Time's up! ${currentQuestion.choices
                .filter((choice) => choice.isCorrect) // Find all correct choices
                .map((choice) => choice.justification) // Extract the text of correct choices
                .join(', ')}`, // Join them as a comma-separated string
        });

        setIsSubmitPhase(true); // Show the "Next" button
    };

    useEffect(() => {
        if (questions.length > 0) {
            const currentTimeLimit = questions[currentQuestionIndex]?.metadata?.timeLimit || 0;
            setTimeRemaining(currentTimeLimit);
        }
    }, [currentQuestionIndex, questions]);

    const handleAnswerSubmit = () => {
        if (
            selectedAnswer === null ||
            (Array.isArray(selectedAnswer) && selectedAnswer.length === 0)
        ) {
            alert('Please select an answer before submitting.');
            return;
        }

        const currentQuestion = questions[currentQuestionIndex];

        let isCorrect = false;
        let justification = '';

        switch (currentQuestion.content.answerType) {
            case 'multiple-choice-single': {
                const correctChoice = currentQuestion.choices.find((choice) => choice.isCorrect);
                isCorrect = selectedAnswer === correctChoice.text;
                const selectedChoice = currentQuestion.choices.find((choice) => selectedAnswer === choice.text);
                justification = selectedChoice.justification || (isCorrect ? 'Correct!' : 'Incorrect.');
                break;
            }
            case 'multiple-choice-multiple': {
                const correctChoices = currentQuestion.choices
                    .filter((choice) => choice.isCorrect)
                    .map((choice) => choice.text);
                isCorrect =
                    selectedAnswer.sort().join(',') === correctChoices.sort().join(',');
                justification = isCorrect
                    ? 'Correct!'
                    : `Incorrect. Correct answers: ${correctChoices.join(', ')}`;
                break;
            }
            case 'true-false': {
                const correctChoice = currentQuestion.choices.find((choice) => choice.isCorrect);
                isCorrect = selectedAnswer === correctChoice.text;
                const selectedChoice = currentQuestion.choices.find((choice) => selectedAnswer === choice.text);
                justification = selectedChoice.justification || (isCorrect ? 'Correct!' : 'Incorrect.');
                break;
            }
            //case 'text': {
            //    const correctAnswers = currentQuestion.correctAnswer.map((ans) => ans.text.toLowerCase());
            //    isCorrect = correctAnswers.includes(selectedAnswer.toLowerCase());
            //    justification = isCorrect
            //        ? 'Correct!'
            //        : `Incorrect. Expected answer: ${correctAnswers.join(' or ')}`;
            //    break;
            //}
            //case 'fill-in-the-blank': {
            //    const correctAnswers = currentQuestion.correctAnswer.map((ans) => ans.text.toLowerCase());
            //    isCorrect = correctAnswers.includes(selectedAnswer.toLowerCase());
            //    justification = isCorrect
            //        ? 'Correct!'
            //        : `Incorrect. Correct answer: ${correctAnswers.join(', ')}`;
            //    break;
            //}
            default:
                justification = 'Unsupported question type.';
                break;
        }

        if (isCorrect) {
            setCorrectAnswersCount((prevCount) => prevCount + 1);
        }

        setFeedback({ correct: isCorrect, justification });
        setIsSubmitPhase(true); // Switch to the "Next" phase, stopping the timer
    };

    const handleHint = () => {
        setShowHint((prev) => !prev);
    };

    const handleExit = () => {
        if (window.confirm('Are you sure you want to exit? Progress will be lost.')) {
            onBackToTopic();
        }
    };

    const handleNext = () => {

        setSelectedAnswer([]); // Reset selected answer for the next question
        setFeedback(null); // Clear feedback
        setIsSubmitPhase(false); // Allow timer to run again for the next question

        if (currentQuestionIndex < questions.length - 1) {
            const nextQuestionTimeLimit =
                questions[currentQuestionIndex + 1]?.metadata?.timeLimit || 0;
            setTimeRemaining(nextQuestionTimeLimit); // Reset timer for the next question
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            setIsCompleted(true);
        }
    };

    const resetExercise = () => {
        setCurrentQuestionIndex(0);
        setCorrectAnswersCount(0);
        setTimeRemaining(null);
        setIsCompleted(false);
        setSelectedAnswer([]);
        setShowHint(false);
        setFeedback(null);
        setIsSubmitPhase(false);

        fetchQuestions();
    };

    if (isCompleted) {
        return (
            <div className="exercise-container">
                <div className="exercise-top-bar">
                    <div className="top-bar-content">
                        <button className="close-button btn-remove-style" onClick={onBackToTopic}>
                            <AiOutlineClose size={24} />
                        </button>
                    </div>
                </div>
                <Result topicId={id} type_={type_} totalQuestions={questions.length} correctAnswers={correctAnswersCount} onBackToTopic={onBackToTopic} onRetry={resetExercise} />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="exercise-container">
                <div className="exercise-top-bar">
                    <div className="top-bar-content">
                        <button className="close-button btn-remove-style" onClick={onBackToTopic}>
                            <AiOutlineClose size={24} />
                        </button>
                    </div>
                </div>
                <Loading/>
            </div>
        );
    }

    if (questions.length <= 0) {
        return (
            <div className="exercise-container">
                <div className="exercise-top-bar">
                    <div className="top-bar-content">
                        <button className="close-button btn-remove-style" onClick={onBackToTopic}>
                            <AiOutlineClose size={24} />
                        </button>
                    </div>
                </div>
                <span>
                    No question available at the moment.
                </span>
            </div>
        );
    }

    return (
        <div className="exercise-container">
            {/* Top bar with close button, progress bar, and timer */}
            <div className="exercise-top-bar">
                <div className="top-bar-content">
                    <button className="close-button btn-remove-style" onClick={handleExit}>
                        <AiOutlineClose size={24} />
                    </button>
                    <div className="flex-grow-1 mx-3">
                        {/* Progress bar */}
                        <div className="progress" style={{ height: '12px', borderRadius: '6px' }}>
                            <div
                                className="progress-bar bg-primary"
                                role="progressbar"
                                style={{
                                    width: `${(currentQuestionIndex / questions.length) * 100}%`,
                                    transition: 'width 0.3s ease',
                                }}
                                aria-valuenow={currentQuestionIndex}
                                aria-valuemin="0"
                                aria-valuemax={questions.length}
                            ></div>
                        </div>
                    </div>
                    <div className="timer">
                        <AiOutlineClockCircle />{' '}
                        {timeRemaining !== null
                            ? `${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')}`
                            : '--'}
                    </div>
                </div>
            </div>

            {/* Middle part with question display */}
            <div className="exercise-question-display margin-top-30">
                    {/*//<div style={{ width: '100%', margin: '0 auto' }}>*/}
                    {/*//    <div className="mb-4 d-flex justify-content-center">*/}
                    {/*//        <div className="col-8">*/}
                    {/*//            <Skeleton height={120} />*/}
                    {/*//        </div>*/}
                    {/*//    </div>*/}

                    {/*//    */}{/* Skeleton for answers */}
                    {/*//    <div className="mb-4">*/}
                    {/*//        */}{/* Row 1 */}
                    {/*//        <div className="d-flex justify-content-center mb-3">*/}
                    {/*//            <div className="col-5 me-3">*/}
                    {/*//                <Skeleton height={80} />*/}
                    {/*//            </div>*/}
                    {/*//            <div className="col-5">*/}
                    {/*//                <Skeleton height={80} />*/}
                    {/*//            </div>*/}
                    {/*//        </div>*/}
                    {/*//        */}{/* Row 2 */}
                    {/*//        <div className="d-flex justify-content-center">*/}
                    {/*//            <div className="col-5 me-3">*/}
                    {/*//                <Skeleton height={80} />*/}
                    {/*//            </div>*/}
                    {/*//            <div className="col-5">*/}
                    {/*//                <Skeleton height={80} />*/}
                    {/*//            </div>*/}
                    {/*//        </div>*/}
                    {/*//    </div>*/}
                    {/*//</div>*/}

                {questions.length > 0 && (
                    <Question
                        question={questions[currentQuestionIndex]}
                        selectedAnswer={selectedAnswer}
                        feedback={feedback}
                        onSelectAnswer={(answer) => {
                            const answerType = questions[currentQuestionIndex].content.answerType;
                            if (answerType === 'multiple-choice-multiple') {
                                setSelectedAnswer((prev) => {
                                    // Ensure prev is always an array
                                    const currentAnswers = Array.isArray(prev) ? prev : [];

                                    // Toggle the answer in the array
                                    if (currentAnswers.includes(answer)) {
                                        return currentAnswers.filter((ans) => ans !== answer); // Remove if already selected
                                    } else {
                                        return [...currentAnswers, answer]; // Add if not selected
                                    }
                                });
                            } else {
                                setSelectedAnswer(answer); // For single-choice questions, directly set the answer
                            }
                        }}
                    />
                )}
                
            </div>

            { type_ === 'exercise' && (
                <div className={`hint-box font-3 ${showHint ? 'show' : ''}`}>
                    <AiOutlineBulb />{' '}
                    {questions[currentQuestionIndex]?.hints?.[0] || 'No hints available for this question.'}
                </div>
            )}

            {/* Bottom bar with hint and submit button */}
            <div className="exercise-bottom-bar">
                <div className="bottom-bar-content">
                    <button
                        className={`btn ${isSubmitPhase ? 'btn-primary' : 'btn-success'} submit-button`}
                        onClick={isSubmitPhase ? handleNext : handleAnswerSubmit}
                    >
                        {isSubmitPhase ? (
                            <>
                                <AiOutlineCheck size={20} /> Next
                            </>
                        ) : (
                            <>
                                <AiOutlineCheck size={20} /> Submit
                            </>
                        )}
                    </button>
                    {type_ === 'exercise' && (
                        <button className="btn btn-secondary hint-button" onClick={handleHint}>
                            <AiOutlineBulb size={20} /> Hint
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
